import './auth-mode-init'

/**
 * ClaimShield Portal - Client Portal Entry Point
 *
 * Serves: portal.claimshieldpro.com
 * Audience: Client users (trucking companies, OSHA clients)
 */
import { createApp } from 'vue'
import { createPinia } from 'pinia'

// PrimeVue
import PrimeVue from 'primevue/config'
import Aura from '@primevue/themes/aura'
import { definePreset } from '@primevue/themes'
import ToastService from 'primevue/toastservice'
import ConfirmationService from 'primevue/confirmationservice'
import Tooltip from 'primevue/tooltip'

// PrimeVue components
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import Card from 'primevue/card'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Dialog from 'primevue/dialog'
import Drawer from 'primevue/drawer'
import Toast from 'primevue/toast'
import Menu from 'primevue/menu'
import Avatar from 'primevue/avatar'
import Badge from 'primevue/badge'
import Tag from 'primevue/tag'
import ProgressSpinner from 'primevue/progressspinner'
import Skeleton from 'primevue/skeleton'
import Dropdown from 'primevue/dropdown'
import DatePicker from 'primevue/datepicker'
import FileUpload from 'primevue/fileupload'
import Select from 'primevue/select'
import Checkbox from 'primevue/checkbox'
import Popover from 'primevue/popover'
import SelectButton from 'primevue/selectbutton'

// App and router
import App from './App.vue'
import { router } from './router'
import { installErrorCapture, posthogPlugin, trackingPlugin } from '@claimshield/shared'

// Styles
import '@claimshield/shared/styles'
import 'primeicons/primeicons.css'
// Refined-light design adoption (sketch 066 variant A, Ahmed 2026-07-31) —
// PORTAL-ONLY: ops is untouched until the Dugger same-core-look review.
// Tabler = sketch 065 winner (nav icons); Nunito Sans = the first wired
// font on the platform (main.css:1-7 documents why none existed).
import '@tabler/icons-webfont/dist/tabler-icons.min.css'
import '@fontsource/nunito-sans/400.css'
import '@fontsource/nunito-sans/600.css'
import '@fontsource/nunito-sans/700.css'
import '@fontsource/nunito-sans/800.css'
import './assets/portal-theme.css'

/**
 * Aura ships with an EMERALD primary. Left unconfigured — as this app was until
 * 2026-07-19 — every PrimeVue control (Button, Checkbox, RadioButton, Switch,
 * Slider, DatePicker selection, Paginator highlight, ProgressSpinner, focus
 * ring) renders green inside a ClaimShield-purple UI, because Tailwind's
 * `primary-*` and PrimeVue's `--p-primary-*` are two unrelated scales.
 *
 * This maps PrimeVue's semantic primary onto the same purple ramp Tailwind
 * uses, so `bg-primary-700` and a PrimeVue Button finally agree. Values mirror
 * packages/shared/tailwind-preset.js → colors.primary and tokens.css exactly;
 * keep the three in sync.
 *
 * The ramp alone is not enough (learned 2026-08-11, on ops). Aura resolves
 * `primary.color` to `{primary.500}`, but the brand anchor is #3d2683, which is
 * 700 on our scale — so a ramp-only preset ships purple-500 (#7952b3) as the
 * action colour and leaves the anchor unused. It hides well: focus rings,
 * checkboxes, toggles, radio fills, tab underlines and links all take the 500,
 * and #7952b3 is a legitimate brand ramp value, so it reads as "purple, fine".
 * The `colorScheme.light.primary` pin below lands every primary-derived surface
 * on the anchor instead. Same block as packages/ops/src/main.ts; keep the four
 * in sync (ops, portal, driver-portal, marketing).
 *
 * `highlight` is deliberately NOT overridden — Aura's defaults already resolve
 * to {primary.50}/{primary.700} = #f5f3fa/#3d2683, exactly the canon's
 * table-header spec. focusRing's geometry is left alone too: it inherits
 * primary.color, so it turns purple without weakening the 1px/2px-offset ring
 * that is the keyboard-focus affordance.
 *
 * Full analysis: knowledge/platform-shared/specs/2026-07-19-design-token-inventory.md
 */
const ClaimShieldAura = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#f5f3fa',
      100: '#ebe6f5',
      200: '#d4c9eb',
      300: '#b8a3dc',
      400: '#9575c9',
      500: '#7952b3',
      600: '#6b4faf',
      700: '#3d2683',
      800: '#2a1a5e',
      900: '#1c1240',
      950: '#0e0920',
    },
    colorScheme: {
      light: {
        primary: {
          color: '{primary.700}',
          contrastColor: '#ffffff',
          hoverColor: '{primary.800}',
          activeColor: '{primary.900}',
        },
      },
    },
  },
})

// Create Vue app
const app = createApp(App)

// Pinia state management
const pinia = createPinia()
app.use(pinia)

// PrimeVue with Aura theme, re-pointed at the ClaimShield purple scale
app.use(PrimeVue, {
  theme: {
    preset: ClaimShieldAura,
    options: {
      prefix: 'p',
      darkModeSelector: '.dark-mode',
      cssLayer: {
        name: 'primevue',
        order: 'tailwind-base, primevue, tailwind-utilities',
      },
    },
  },
  ripple: true,
})

// PrimeVue services
app.use(ToastService)
app.use(ConfirmationService)

// PrimeVue directive
app.directive('tooltip', Tooltip)

// Register PrimeVue components globally
app.component('PButton', Button)
app.component('PInputText', InputText)
app.component('PPassword', Password)
app.component('PCard', Card)
app.component('PDataTable', DataTable)
app.component('PColumn', Column)
app.component('PDialog', Dialog)
app.component('PDrawer', Drawer)
app.component('PToast', Toast)
app.component('PMenu', Menu)
app.component('PAvatar', Avatar)
app.component('PBadge', Badge)
app.component('PTag', Tag)
app.component('PProgressSpinner', ProgressSpinner)
app.component('PSkeleton', Skeleton)
app.component('PDropdown', Dropdown)
app.component('PDatePicker', DatePicker)
app.component('PCalendar', DatePicker)  // PrimeVue 4 renamed Calendar to DatePicker
app.component('PFileUpload', FileUpload)
app.component('PSelect', Select)
app.component('PCheckbox', Checkbox)
app.component('PPopover', Popover)
app.component('PSelectButton', SelectButton)

// Route-loading hooks are installed by the router module before this starts
// initial navigation; Vue replaces the document shell with App's boundary.
app.use(router)

// Product analytics — the portal had no hook until 2026-08. Invisible to the
// carrier; only admins can read any of it.
app.use(trackingPlugin, { router, app: 'portal' })

// PostHog — cross-surface funnels, alongside (not replacing) the tracker
// above; the split between them is knowledge/product-analytics/TRACK.md §7.1.
// This is a no-op unless the hostname is on the allowlist inside the plugin.
// Phase 1 that means demo.claimshieldpro.com ONLY — the demo serves this same
// image against synthetic carriers, so the pipe gets proven with no real
// driver record involved. portal.claimshieldpro.com joins in Phase 3.
app.use(posthogPlugin, { router, app: 'portal' })

// Global JS error capture — must run after Pinia is registered so the
// capture handler can read the auth store when reporting.
installErrorCapture(app)

// Mount
app.mount('#app')
